/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	createEmptyScope,
	MEDIA_RESOLUTIONS,
	RuntimeContext,
	RUNTIME_CONTEXT_MODE,
	TGenericBlueprintSchema,
	TGetBlueprintSchemaSpec
} from "@adapptio/lib-blueprint";
import {
	IIconPackageURLMapping,
	ToastMessageManager,
	OverlayManager,
	TChildViewFactoryFn,
	TRootComponentListSchema
} from "@adapptio/lib-components";
import {
	CoreComponentEventNodeTypes,
	GlobalsContainer,
	GlobalsResolver,
	IComponentDefRegistry,
	ITranslationManager,
	IViewGlobals,
	createCoreComponentEventHandlers,
	IRenderContext,
	IEventHandlerLogFn,
	IRenderContextResolvers,
	createRootViewRuntime,
	IThemeProvider
} from "@adapptio/lib-core";
import {
	createEventEmitter,
	emitEvent,
	onEvent,
	removeAllEventListeners,
	IViewParamsSpec,
	offEvent
} from "@adapptio/lib-shared";
import { v4 as uuid_v4 } from "uuid";

import { EnvironmentService } from "../environment/EnvironmentService";
import { EditorActionRepository } from "./EditorActionRepository";
import { EditorRoutingManager } from "./EditorRoutingManager";
import { EditorSessionProvider } from "./EditorSessionProvider";
import { ComponentEventResolver } from "./Resolvers/ComponentEventResolver";
import { IRuntimeServiceBaseResolvers } from "./RuntimeService";
import { IAppInfo } from "../common/AppInfo";
import { AppConfigService } from "../config/AppConfigService";

export type TEditorRenderContextGetCmpList<TSchema extends TGenericBlueprintSchema> = (
	spec: TGetBlueprintSchemaSpec<TSchema>
) => TGetBlueprintSchemaSpec<TRootComponentListSchema>;

export interface IEditorRenderContextOpts_RootView {
	viewName: string;
	viewParams: IViewParamsSpec;
}

/**
 * Editor render context options
 */
export interface IEditorRenderContextOpts<TSchema extends TGenericBlueprintSchema> {
	mode: RUNTIME_CONTEXT_MODE;
	mediaResolution: keyof typeof MEDIA_RESOLUTIONS;
	baseResolvers: IRuntimeServiceBaseResolvers;
	componentRegistry: IComponentDefRegistry;
	actionRepository: EditorActionRepository;
	routingManager: EditorRoutingManager;
	translationManager: ITranslationManager;
	iconPackageUrlMapping: IIconPackageURLMapping;
	environmentService: EnvironmentService;
	eventLogFn: IEventHandlerLogFn;
	getComponentListSpec: TEditorRenderContextGetCmpList<TSchema>;
	appInfo: IAppInfo;
	appConfigService: AppConfigService;
	themeProvider: IThemeProvider;
	/**
	 * Root view - if specified the root view runtime context will be created,
	 * otherwise runtime context with dummy render functions will be created instead.
	 */
	rootView?: IEditorRenderContextOpts_RootView;
	withPrivateConstants?: boolean;
}

/**
 * Client render context
 */
export class EditorRenderContext<TSchema extends TGenericBlueprintSchema> implements IRenderContext {
	/** Runtime mode */
	public mode: RUNTIME_CONTEXT_MODE;

	/** Media resolution */
	public mediaResolution: keyof typeof MEDIA_RESOLUTIONS;

	/** Component registry */
	public componentRegistry: IComponentDefRegistry;

	/** Routing manager */
	public routingManager: EditorRoutingManager;

	/** Overlay manager */
	public overlayManager: OverlayManager;

	/** Toast Message manager */
	public toastMessageManager: ToastMessageManager;

	/** Icon package URL mapping */
	public iconPackageUrlMapping: IIconPackageURLMapping = {};

	/** Translate function */
	public translateFn: IRenderContext["translateFn"];

	public readonly themeProvider: IThemeProvider;

	/** Emitted when runtime context has rendered */
	public onRender = createEventEmitter<void>();

	/** Emitted when mode has changed */
	public onModeChange = createEventEmitter<void>();

	/** Emitted when media resoluton has changed */
	public onMediaResolutionChange = createEventEmitter<void>();

	/** Root Runtime context */
	private rCtx: RuntimeContext<TSchema>;

	/** Resolvers */
	private resolvers: IRenderContextResolvers;

	/** Action repository */
	private actionRepository: EditorActionRepository;

	/** Session provider */
	private sessionProvider: EditorSessionProvider;

	/** Globals container */
	private globalsContainer: GlobalsContainer<IViewGlobals>;

	/** Event logging function */
	private eventLogFn: IEventHandlerLogFn;

	/** Function to get root component list from spec */
	private componentListGetter: TEditorRenderContextGetCmpList<TSchema>;

	private appInfo: IAppInfo;

	private appConfigSvc: AppConfigService;

	private environmentSvc: EnvironmentService;

	/**
	 * Creates child root view and runtime context
	 * WARNING: You must destroy child runtime context yourself using `runtimeContext.destroy()`!
	 * NOTE: Intentionally defined as property so it can be safely passed to other code parts.
	 *
	 * @param viewName View name
	 * @param viewParams View params
	 */
	private childViewFactory: TChildViewFactoryFn;

	private withPrivateConstants = false;

	/**
	 * Context constructor
	 *
	 * @param config Context options
	 */
	public constructor(config: IEditorRenderContextOpts<TSchema>) {
		this.mode = config.mode;
		this.mediaResolution = config.mediaResolution;

		this.componentRegistry = config.componentRegistry;
		this.actionRepository = config.actionRepository;
		this.iconPackageUrlMapping = config.iconPackageUrlMapping;
		this.routingManager = config.routingManager;
		this.themeProvider = config.themeProvider;
		this.sessionProvider = new EditorSessionProvider(config.environmentService);
		this.componentListGetter = config.getComponentListSpec;
		this.eventLogFn = config.eventLogFn;

		this.environmentSvc = config.environmentService;

		this.appInfo = config.appInfo;

		this.appConfigSvc = config.appConfigService;
		this.withPrivateConstants = config.withPrivateConstants || false;

		this.setupGlobalsContainer();

		const eventHandlers = this.createEventHandlers();
		const componentEventResolver = new ComponentEventResolver(CoreComponentEventNodeTypes, eventHandlers);

		this.translateFn = (domain, term, vars, fallbackValue) => {
			// @todo !!! Change language to a user's one!
			return config.translationManager.translate(domain, "en_US", term, vars, fallbackValue);
		};

		// Add resolvers
		this.resolvers = {
			...config.baseResolvers,
			componentEvent: componentEventResolver,
			globals: new GlobalsResolver(this.globalsContainer),
			route: config.routingManager.routeResolver,
			navigation: config.routingManager.navigationResolver,

			translate: {
				translate: (term, vars, fallbackValue, domain) => {
					return this.translateFn(domain || "app", term, vars, fallbackValue);
				}
			}
		};

		// Create runtime context
		const rCtxPath = [ uuid_v4() ];

		if (config.rootView) {
			this.rCtx = createRootViewRuntime(
				this.resolvers,
				config.rootView.viewName,
				config.rootView.viewParams,
				null,
				rCtxPath,
				true
			) as unknown as RuntimeContext<TSchema>; // re-type is mandatory here
		} else {
			this.rCtx = new RuntimeContext(
				{
					resolvers: this.resolvers,
					mode: this.mode,
					inEditor: true,
					asyncRenderTimeout: -1,
					path: rCtxPath
				},
				() => null,
				createEmptyScope()
			);
		}

		onEvent(this.rCtx.renderEvent, () => emitEvent(this.onRender));

		// Create child view factory
		this.childViewFactory = (viewName: string, viewParams: IViewParamsSpec) => {
			return createRootViewRuntime(this.resolvers, viewName, viewParams, this.rCtx);
		};

		this.overlayManager = new OverlayManager(this.childViewFactory);

		this.toastMessageManager = new ToastMessageManager();
	}

	private getConstants(): Record<string, string | number | boolean | null> {
		if (this.withPrivateConstants) {
			return {
				...this.appConfigSvc.getConfig()?.publicConstants,
				...this.appConfigSvc.getConfig()?.privateConstants
			};
		}

		return this.appConfigSvc.getConfig()?.publicConstants;
	}

	/**
	 * Creates global container and setup event listeners
	 */
	private setupGlobalsContainer() {
		// Unsubscribe possible previous listeners
		this.unsubscribeContainerUpdaters();

		this.globalsContainer = new GlobalsContainer<IViewGlobals>({
			location: this.routingManager.getCurrentLocation(),
			session: this.sessionProvider.getSession(),
			currentUser: this.sessionProvider.getIdentity(),
			appName: this.appInfo.name,
			constants: this.getConstants(),
			_app: this.appConfigSvc.getConfig()?._app
		});

		// Update globals on changes and set defaults
		this.subscribeContainerUpdaters();
	}

	private refreshContainerLocation = (): void => {
		this.globalsContainer.set("location", this.routingManager.getCurrentLocation());
	};

	private refreshContainerSessionData = (): void => {
		this.globalsContainer.set("session", this.sessionProvider.getSession());
		this.globalsContainer.set("currentUser", this.sessionProvider.getIdentity());
	};

	private refreshContainerConstants = (): void => {
		this.globalsContainer.set("constants", this.appConfigSvc.getConfig()?.publicConstants);
		this.globalsContainer.set("_app", this.appConfigSvc.getConfig()?._app);
	};

	private subscribeContainerUpdaters = (): void => {
		onEvent(this.routingManager.onNavigate, this.refreshContainerLocation);
		onEvent(this.sessionProvider.onChange, this.refreshContainerSessionData);
		onEvent(this.appConfigSvc.onChange, this.refreshContainerConstants);
	};

	private unsubscribeContainerUpdaters = (): void => {
		offEvent(this.routingManager.onNavigate, this.refreshContainerLocation);
		offEvent(this.sessionProvider.onChange, this.refreshContainerSessionData);
		offEvent(this.appConfigSvc.onChange, this.refreshContainerConstants);
	};

	private createEventHandlers() {
		return createCoreComponentEventHandlers({
			logEvent: this.eventLogFn,
			getActionDelegate: (actionId, params) => {
				return this.actionRepository.getDelegate(actionId, params);
			},
			reloadActionDelegates: (actionId, paramsMask, waitForData) => {
				return this.actionRepository.reloadDelegates(actionId, paramsMask, waitForData);
			},
			navigate: (linkSpec, opts, openInNew) => {
				const target = this.routingManager.resolveLink(linkSpec, opts);

				if (openInNew) {
					window.open(target.url);
				} else {
					this.routingManager.navigate(target.url);
				}
			},
			navigateBack: () => {
				this.routingManager.navigate(-1);
			},
			setPreventNavigation: (prevent) => {
				this.routingManager.setPreventNavigation(prevent);
			},
			openOverlay: async (data) => {
				const { overlayId, buttonId, customData } = await this.overlayManager.itemIsResolved(
					this.overlayManager.addItemData(data)
				);

				return {
					overlayId,
					buttonId,
					customData
				};
			},
			closeOverlay: (overlayId, buttonId, customData) => {
				this.overlayManager.removeItem(overlayId, buttonId, customData);
			},
			showToastMessage: (data) => {
				return this.toastMessageManager.addItemData(data);
			},
			hideToastMessage: (toastMessageId) => {
				this.toastMessageManager.removeItem(toastMessageId);
			},
			reloadSession: async (reloadUserAccount?: boolean) => {
				await this.sessionProvider.reload(reloadUserAccount);
			}
		});
	}

	/**
	 * Returns component list spec
	 */
	public getComponentListSpec(): TGetBlueprintSchemaSpec<TRootComponentListSchema> {
		return this.componentListGetter(this.rCtx.getLastSpec());
	}

	/**
	 * Returns runtime context
	 */
	public getRuntimeContext(): RuntimeContext {
		return this.rCtx;
	}

	/**
	 * Returns resolvers
	 */
	public getResolvers(): IRenderContextResolvers {
		return this.resolvers;
	}

	/**
	 * Returns globals container
	 */
	public getGlobalsContainer(): GlobalsContainer<IViewGlobals> {
		return this.globalsContainer;
	}

	/**
	 * Sets the context mode
	 * @param mode Context mode
	 */
	public setMode(mode: RUNTIME_CONTEXT_MODE): void {
		this.mode = mode;
		this.rCtx.setMode(mode);

		emitEvent(this.onModeChange);
	}

	/**
	 * Sets the context mode
	 * @param mode Context mode
	 */
	public setMediaResolution(mediaResolution: keyof typeof MEDIA_RESOLUTIONS): void {
		this.mediaResolution = mediaResolution;
		emitEvent(this.onMediaResolutionChange);
	}

	/**
	 * Destroys the render context
	 */
	public destroy(): void {
		this.unsubscribeContainerUpdaters();

		this.overlayManager.removeAllItems();
		this.toastMessageManager.removeAllItems();

		this.globalsContainer.destroy();
		this.rCtx.destroy();
		this.sessionProvider.dispose();

		this.resolvers = null;
		this.translateFn = null;
		this.globalsContainer = null;
		this.rCtx = null;

		removeAllEventListeners(this.onRender);
		removeAllEventListeners(this.onModeChange);
		removeAllEventListeners(this.onMediaResolutionChange);
	}
}
